import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments';

const GOOGLE_PAY_SCRIPT_URL = 'https://pay.google.com/gp/p/js/pay.js';

/** Google Pay uses PRODUCTION for prod-us/prod-eu, TEST otherwise. */
const PROD_ENVS = new Set(['prod-us', 'prod-eu', 'staging']);

/** Minimal types for Google Pay API (loaded via script). */
declare global {
  interface Window {
    google?: {
      payments?: {
        api?: {
          PaymentsClient: new (options: PaymentClientOptions) => PaymentsClient;
        };
      };
    };
  }
}

interface PaymentClientOptions {
  environment?: 'TEST' | 'PRODUCTION';
}

interface PaymentsClient {
  isReadyToPay(request: IsReadyToPayRequest): Promise<IsReadyToPayResponse>;
  loadPaymentData(request: PaymentDataRequest): Promise<PaymentData>;
  createButton(options: ButtonOptions): HTMLElement;
}

interface IsReadyToPayRequest {
  apiVersion: number;
  apiVersionMinor: number;
  allowedPaymentMethods: unknown[];
}

interface IsReadyToPayResponse {
  result?: boolean;
  paymentMethodPresent?: boolean;
}

interface PaymentDataRequest {
  apiVersion: number;
  apiVersionMinor: number;
  merchantInfo: { merchantId: string; merchantName?: string };
  allowedPaymentMethods: PaymentMethodSpec[];
  transactionInfo: TransactionInfo;
}

interface PaymentMethodSpec {
  type: string;
  parameters: Record<string, unknown>;
  tokenizationSpecification: TokenizationSpecification;
}

interface TokenizationSpecification {
  type: string;
  parameters: Record<string, string>;
}

interface TransactionInfo {
  countryCode: string;
  currencyCode: string;
  totalPriceStatus: 'NOT_CURRENTLY_KNOWN' | 'ESTIMATED' | 'FINAL';
  totalPrice: string;
}

export interface PaymentData {
  paymentMethodData?: {
    tokenizationData?: {
      token: string;
    };
    info?: { cardNetwork?: string; cardDetails?: string };
  };
  email?: string;
  shippingAddress?: unknown;
}

interface ButtonOptions {
  buttonColor?: 'default' | 'black' | 'white';
  buttonType?: 'long' | 'short';
  buttonSizeMode?: 'fill' | 'static';
  onClick?: (event: Event) => void;
  allowedPaymentMethods?: unknown[];
}

export interface TapGooglePayRequestConfig {
  /** Tap Merchant ID (used as gatewayMerchantId in tokenization). */
  gatewayMerchantId: string;
  /** Amount in major units (e.g. 10.50). */
  amount: number;
  /** Currency code (e.g. USD, AED, KWD). */
  currencyCode: string;
  /** Country code (e.g. US, AE, KW). */
  countryCode?: string;
  /** Display name for the merchant. */
  merchantName?: string;
}

/**
 * Service for Google Pay Web API integration with Tap Payments.
 * Loads the Google Pay script and builds PaymentDataRequest with gateway "tappayments".
 * Does not perform HTTP; caller uses the token with TapPaymentService.createCharge().
 * SSR-safe: all use of window/document is guarded with isPlatformBrowser(PLATFORM_ID).
 */
@Injectable({
  providedIn: 'root',
})
export class GooglePayService {
  private readonly platformId = inject(PLATFORM_ID);
  private scriptLoadPromise: Promise<void> | null = null;

  /** Load Google Pay script once. */
  loadScript(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }
    this.scriptLoadPromise = new Promise((resolve, reject) => {
      if (typeof document === 'undefined') {
        resolve();
        return;
      }
      if (document.querySelector(`script[src="${GOOGLE_PAY_SCRIPT_URL}"]`)) {
        if ((window as Window).google?.payments?.api) {
          resolve();
        } else {
          (window as Window).addEventListener('load', () => resolve(), { once: true });
        }
        return;
      }
      const script = document.createElement('script');
      script.src = GOOGLE_PAY_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Pay script'));
      document.head.appendChild(script);
    });
    return this.scriptLoadPromise;
  }

  /**
   * Build PaymentDataRequest for Tap (PAYMENT_GATEWAY with tappayments).
   * Amount is formatted as string (e.g. "10.50") per Google Pay API.
   */
  buildPaymentDataRequest(config: TapGooglePayRequestConfig): PaymentDataRequest {
    const totalPrice =
      typeof config.amount === 'number' ? config.amount.toFixed(2) : String(config.amount);

    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      merchantInfo: {
        merchantId: environment.googlePayMerchantId,
        merchantName: config.merchantName ?? 'Merchant',
      },
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'JCB', 'INTERAC'],
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'tappayments',
              gatewayMerchantId: config.gatewayMerchantId,
            },
          },
        },
      ],
      transactionInfo: {
        countryCode: config.countryCode ?? 'US',
        currencyCode: config.currencyCode,
        totalPriceStatus: 'FINAL',
        totalPrice,
      },
    };
  }

  /**
   * Build minimal request for isReadyToPay (same allowedPaymentMethods structure).
   */
  buildIsReadyToPayRequest(
    config: Pick<TapGooglePayRequestConfig, 'gatewayMerchantId'>
  ): IsReadyToPayRequest {
    return {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'JCB', 'INTERAC'],
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'tappayments',
              gatewayMerchantId: config.gatewayMerchantId,
            },
          },
        },
      ],
    };
  }

  /**
   * Get PaymentsClient (TEST or PRODUCTION). Call after loadScript().
   * Defaults to PRODUCTION when environment.env is prod-us or prod-eu, otherwise TEST.
   * Pass an explicit value to override (e.g. in tests).
   */
  getPaymentsClient(googlePayEnv?: 'TEST' | 'PRODUCTION'): PaymentsClient | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const api = (window as Window).google?.payments?.api;
    if (!api?.PaymentsClient) return null;
    const env = googlePayEnv ?? (PROD_ENVS.has(environment.env) ? 'PRODUCTION' : 'TEST');
    return new api.PaymentsClient({ environment: env });
  }

  /**
   * Check if the user can pay with Google Pay.
   */
  async isReadyToPay(
    config: Pick<TapGooglePayRequestConfig, 'gatewayMerchantId'>
  ): Promise<boolean> {
    await this.loadScript();
    const client = this.getPaymentsClient();
    if (!client) return false;
    const request = this.buildIsReadyToPayRequest(config);
    try {
      const response = await client.isReadyToPay(request);
      return response?.result === true;
    } catch {
      return false;
    }
  }

  /**
   * Load payment data (call from user gesture, e.g. button click).
   * Returns the token from paymentData.paymentMethodData.tokenizationData.token.
   */
  async loadPaymentData(config: TapGooglePayRequestConfig): Promise<PaymentData> {
    await this.loadScript();
    const client = this.getPaymentsClient();
    if (!client) {
      throw new Error('Google Pay is not available');
    }
    const request = this.buildPaymentDataRequest(config);
    return client.loadPaymentData(request);
  }

  /**
   * Extract Tap token from Google Pay PaymentData.
   * Token is under paymentData.paymentMethodData.tokenizationData.token (per Tap docs).
   */
  getTokenFromPaymentData(paymentData: PaymentData): string | null {
    const token = paymentData?.paymentMethodData?.tokenizationData?.token;
    if (typeof token === 'string' && token.length > 0) {
      return token;
    }
    if (token != null && typeof token === 'object') {
      const str = JSON.stringify(token);
      if (str.length > 0) return str;
    }
    return null;
  }

  /**
   * Create and return the Google Pay button element. Caller must append to DOM.
   */
  createButton(
    onClick: (event: Event) => void,
    options: { buttonColor?: 'default' | 'black' | 'white'; buttonType?: 'long' | 'short' } = {}
  ): HTMLElement | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const client = this.getPaymentsClient();
    if (!client) return null;
    return client.createButton({
      buttonColor: options.buttonColor ?? 'default',
      buttonType: options.buttonType ?? 'long',
      buttonSizeMode: 'fill',
      onClick,
    });
  }
}
